import { RootState } from '../index';
import {
  OnBoardingInfo,
  OnBoardingMap,
  OnBoardingQuestion,
  OnBoardingSelectedAnswers,
  OnBoardingStepType,
} from './types';
import { createSelector } from '@reduxjs/toolkit';

export const getCurrentStep = (state: RootState): OnBoardingMap | null => state.onBoarding.currentStep;

export const getAllSteps = (state: RootState): OnBoardingStepType[] | null => state.onBoarding.steps;

export const getAllSelectedAnswers = (state: RootState): OnBoardingSelectedAnswers | null =>
  state.onBoarding.selectedAnswers;

export const getNextStep = createSelector(
  getCurrentStep,
  getAllSteps,
  (currentStep, nextSteps): OnBoardingStepType | undefined => {
    const currentIndex = nextSteps?.findIndex((s) => s.type === currentStep?.type && s.id === currentStep?.id);

    if (currentIndex === undefined || currentIndex < 0) return nextSteps?.slice(0, 1)[0];

    return nextSteps?.slice(currentIndex + 1, currentIndex + 2)[0];
  }
);

export const getPreviewSteps = createSelector(
  getCurrentStep,
  getAllSteps,
  (currentStep, allSteps): OnBoardingStepType[] | undefined => {
    const currentIndex = allSteps?.findIndex((s) => s.type === currentStep?.type && s.id === currentStep?.id);

    if (currentIndex === undefined || currentIndex < 0) return [];

    return allSteps?.slice(0, currentIndex);
  }
);

export const getQuestionById =
  (id: string) =>
  (state: RootState): OnBoardingQuestion | undefined =>
    state.onBoarding.questions.find((q) => q.id === id);

export const getInfoById =
  (id: string) =>
  (state: RootState): OnBoardingInfo | undefined =>
    state.onBoarding.infos.find((q) => q.id === id);

export const getSelectedAnswersByQuestionId = (id: string) =>
  createSelector(getAllSelectedAnswers, (answers) => answers?.[id]);

export const getOnBoardingMap = (state: RootState): OnBoardingMap[] => state.onBoarding.map_data;

export const getAnswerPopup = (questionId: string) =>
  createSelector(getQuestionById(questionId), getSelectedAnswersByQuestionId(questionId), (question, selected) =>
    question?.is_multiple ? null : question?.answers?.find((a) => a.id === selected?.[0])?.popup
  );

export const getAvailableVars = (state: RootState) => state.onBoarding.available_vars;

export const getVars = (state: RootState) => state.onBoarding.vars;

export const isLoading = (state: RootState) => state.onBoarding.isLoading;
export const getOnBoardingID = (state: RootState) => state.onBoarding.id;
export const getAnswerConditionsSelector = (state: RootState) => state.onBoarding?.answer_conditions ?? null;
export const getMainPageData = (state: RootState) => state.onBoarding.main_page;
export const getOnBoardingName = (state: RootState) => state.onBoarding.name;

export const getOnBoardingKey = (state: RootState) => state.onBoarding.onboarding_key;

export const getStepNumber = createSelector(getCurrentStep, getAllSteps, (currentStep, allSteps): number => {
  if (!currentStep) return allSteps?.length ?? 1;

  const index = allSteps?.findIndex((s) => s.type === currentStep.type && s.id === currentStep.id);

  return index === undefined || index === -1 ? 0 : index + 1;
});
export const getMainPageButtonsData = (state: RootState) => ({
  button_label: state.onBoarding.button_label,
  show_additional_button: state.onBoarding.show_additional_button,
  additional_button_label: state.onBoarding.additional_button_label,
});
export const getOnBoardingReviewsSelector = (state: RootState) => state.onBoarding.reviews;
export const getRegisterUserID = (state: RootState) => state.onBoarding.register_user_id;
export const getRedirectToken = (state: RootState) => state.onBoarding.redirect_token;
export const getOnBoardingMainPageSettings = (state: RootState) => ({
  show_close_button: state.onBoarding.show_close_button,
  show_mindly_logo: state.onBoarding.show_mindly_logo,
});
export const getOnBoardingLocale = (state: RootState) => state.onBoarding.lang;